import {
  getBackgroundShade,
  getColorFromString,
  getShade,
  IColor,
  IPalette,
  registerIcons,
  Shade,
  unregisterIcons,
} from '@fluentui/react';

import { v4 as uuidv4 } from 'uuid';
import { InAppSupportedArticles, SMCTicketing } from '../models/constants/getHelpConstants';
import { appContext } from '../models/getHelp/appContext';
import { getEnglishString, getString } from './stringLoader';
import { AuthTokenRequest, EmeraldSigninType, TokenType } from '../models/getHelp/getHelpAuthToken';
import { inEmsSession } from '../../common/utils/commonUtilities';
import { GetAuthTokenPurpose } from '../models/getHelp/getAuthTokenPurpose';
import { isWindowsCopilotRequestCreationDeeplinkKey } from '../../web-legacy/models/constants/constants';

export const initializeGetHelpIcons = () => {
  unregisterIcons([
    'Feedback',
    'FavoriteStar',
    'FavoriteStarFill',
    'Info',
    'InfoSolid',
    'WarningSolid',
    'CompletedSolid',
  ]);
  registerIcons({
    fontFace: {
      fontFamily: `"Get Help Fluent Icons"`,
      src: `url('${appContext.distUrl}/assets/fonts/GetHelpIcons20220307.woff2') format('woff2')`,
    },
    icons: {
      Feedback: '\uED15', // feedback icon
      FavoriteStar: '\uE734', // rounded star
      FavoriteStarFill: '\uE735', // rounded solid star
      Info: '\uE946', // i for information
      InfoSolid: '\uF167', // Solid i for information
      WarningSolid: '\uF5C9', // Solid ! for warning
      ErrorSolid: '\uEB90', // Solid X for error
      CompletedSolid: '\uEC61', // Solid check mark for completed
    },
  });
};

/**
 * Generate palette based on accent color.
 * @param color color hex string is rrggbb format
 * @returns A partial of IPalette based on the given accent color
 */
export const createPalette = (color: string, theme: string): Partial<IPalette> => {
  let accentColor: IColor = getColorFromString(`#${color}`);
  if (!accentColor) {
    // Set as default accentColor if failed to parse from string
    accentColor = getColorFromString(`#0078D4`);
  }

  let foregroundColor: IColor = getColorFromString('#000000');
  let backgroundColor: IColor = getColorFromString('#ffffff');
  let isInverted = false;

  if (theme === 'Dark') {
    foregroundColor = getColorFromString('#ffffff');
    backgroundColor = getColorFromString('#000000');
    isInverted = true;
  }

  return {
    themePrimary: `#${getShade(accentColor, Shade.Shade6, isInverted).hex}`,
    themeLighterAlt: `#${getShade(accentColor, Shade.Shade1, isInverted).hex}`,
    themeLighter: `#${getShade(accentColor, Shade.Shade2, isInverted).hex}`,
    themeLight: `#${getShade(accentColor, Shade.Shade3, isInverted).hex}`,
    themeTertiary: `#${getShade(accentColor, Shade.Shade4, isInverted).hex}`,
    themeSecondary: `#${getShade(accentColor, Shade.Shade5, isInverted).hex}`,
    themeDarkAlt: `#${getShade(accentColor, Shade.Shade6, isInverted).hex}`,
    themeDark: `#${getShade(accentColor, Shade.Shade7, isInverted).hex}`,
    themeDarker: `#${getShade(accentColor, Shade.Shade8, isInverted).hex}`,

    neutralLighterAlt: `#${getBackgroundShade(backgroundColor, Shade.Shade1, isInverted).hex}`,
    neutralLighter: `#${getBackgroundShade(backgroundColor, Shade.Shade2, isInverted).hex}`,
    neutralLight: `#${getBackgroundShade(backgroundColor, Shade.Shade3, isInverted).hex}`,
    neutralQuaternaryAlt: `#${getBackgroundShade(backgroundColor, Shade.Shade4, isInverted).hex}`,
    neutralQuaternary: `#${getBackgroundShade(backgroundColor, Shade.Shade5, isInverted).hex}`,
    neutralTertiaryAlt: `#${getBackgroundShade(backgroundColor, Shade.Shade6, isInverted).hex}`,
    neutralTertiary: `#${getBackgroundShade(foregroundColor, Shade.Shade3, isInverted).hex}`,
    neutralSecondary: `#${getBackgroundShade(foregroundColor, Shade.Shade5, isInverted).hex}`,
    neutralPrimaryAlt: `#${getBackgroundShade(foregroundColor, Shade.Shade6, isInverted).hex}`,
    neutralPrimary: `#${getBackgroundShade(foregroundColor, Shade.Unshaded, isInverted).hex}`,
    neutralDark: `#${getBackgroundShade(foregroundColor, Shade.Shade7, isInverted).hex}`,

    black: `#${getBackgroundShade(foregroundColor, Shade.Shade8, isInverted).hex}`,
    white: `#${getBackgroundShade(backgroundColor, Shade.Unshaded, isInverted).hex}`,
  };
};

// tslint:disable-next-line:typedef
export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const decodeHtmlEntities = (input: string): string => {
  if (!input || !input.includes('&')) {
    return input;
  }
  const fixedInput = input.replace(/[<>]/g, (c: string) => (c === '<' ? '&lt;' : '&gt'));
  return new DOMParser().parseFromString(fixedInput, 'text/html').documentElement.textContent;
};

export const isArticleSupportedInApp = (path: string) => {
  const [isValidUrl, url] = tryParseUrl(path);

  return (
    isValidUrl &&
    !!url &&
    !!url.hostname &&
    !!InAppSupportedArticles.find(supported => {
      return url.hostname.includes(supported);
    })
  );
};

export const supportDafv1: boolean =
  (appContext.diagcap?.includes('dafv1') || appContext.diagcap?.includes('msdt')) ?? false;

export const supportDafv2: boolean =
  (appContext.diagcap?.includes('dafv2') || appContext.diagcap?.includes('rt')) ?? false;

const getSearchTextAndActualQueryFromDeepLink = (path: string): [string, string] => {
  let searchText: string = null;
  let actualQuery: string = null;
  if (!!path) {
    try {
      const resId = `DeepLink_${path.replace(/\//g, '_').replace(/^_+|_+$/g, '')}`;
      const directResId = `Direct${resId}`;
      const directDeeplinkLocText = getString(directResId, true, true);
      // If found (getString returns string instead of resource id) a direct deeplink,
      // set the loc string as display text and string id as actual query
      if (directDeeplinkLocText !== directResId) {
        searchText = directDeeplinkLocText;
        actualQuery = directResId;
      } else {
        const deeplinkQuery = getString(resId, true, true);
        // If found (getString returns string instead of resource id) a regular deeplink,
        // set the loc string as display text and English string as actual query
        if (deeplinkQuery !== resId) {
          searchText = deeplinkQuery;
          actualQuery = getEnglishString(resId, true);
        }
      }
    } catch (error) {
      console.log(error);
      console.log('error finding deeplink');
      // logError(
      //   `Exception when mapping deeplink: ${error.message} (deeplink was ${path}) Experience: ${
      //     EmeraldUserType[appContext.emeraldUserType]
      //   }`
      // );
    }
  }
  return [searchText, actualQuery];
};

export const tryParseUrl = (urlString: string): [boolean, URL] => {
  try {
    const url = new URL(urlString);
    return [true, url];
  } catch (_) {
    return [false, null];
  }
};

export const getQueryFromDeepLink = (path: string): [string, string] => {
  let searchText: string = null;
  let actualQuery: string = null;
  if (!!path) {
    // Replace the special Audio team deeplink.
    if (path.includes('SearchKey=Audio%20Issues')) {
      path = 'ms-contact-support://settings/AudioTroubleshooter';
    }

    // Check if the path is a url, if it's a url (new version of GetHelp UWP), parse it to a url.
    const [isValidUrl, url] = tryParseUrl(path);
    if (isValidUrl) {
      // Try to extract the pathname in the url to map the loc query
      // If hostname is not falsy, it's the Spartan Edge, use url.hostname + url.pathname
      // Otherwise it's Chrome, use url.pathname and remove leading "//"
      if (!!url.hostname) {
        [searchText, actualQuery] = getSearchTextAndActualQueryFromDeepLink(url.hostname + url.pathname);
      } else {
        [searchText, actualQuery] = getSearchTextAndActualQueryFromDeepLink(url.pathname.substring(2));
      }
      if (searchText === null) {
        // If there is no match, try to get to SearchKey string from url query
        searchText = url.searchParams.get('SearchKey');
        /// For copilot ticket creation deep link, ex: SearchKey=emeraldwindowscopilot-how%20to%20adjust%20brightness
        if (searchText?.toLowerCase().startsWith(isWindowsCopilotRequestCreationDeeplinkKey.toLowerCase())) {
          searchText = searchText.substring(isWindowsCopilotRequestCreationDeeplinkKey.length)
        }
      }
    } else {
      // If the path is not url (old version of GetHelp UWP), use the old way to map
      [searchText, actualQuery] = getSearchTextAndActualQueryFromDeepLink(path);
    }
  }
  return [searchText, actualQuery];
};

export const getRandomGuidString = (): string => uuidv4();

export const getParamFromRelativepath = (relativepath: string, paramName: string): string => {
  // Relative path looks like this - /en-US/contact/enterprise/?manufacturer=Dell%20Inc.&isNative=true&flowId=windows-gethelp&vatheme=smc-lightgrey-native
  // discard part before ?
  let queryString = relativepath?.split('?', 2)[1];
  // discard part after #
  queryString = queryString?.split('#')[0];
  // split key values seperated by &
  const keyvalarr: string[] = queryString?.split('&');

  const keyvalpair = keyvalarr?.find(keyval => {
    const pair = keyval?.split('=');
    return !!pair && pair[0]?.toLowerCase() === paramName;
  });

  const resultPair = keyvalpair?.split('=');
  const value = !!resultPair && resultPair?.length > 1 ? decodeURIComponent(resultPair[1]) : '';
  return value;
};

export const appendQueryParams = (urlString: string): string => {
  const url = new URL(urlString);
  url.searchParams.append('from', 'GetHelpBCQR');
  url.searchParams.append('CorrelationId', appContext.sessionId);
  return url.toString();
};

export const buildJsllStringForClick = (
  message: string,
  locale: string,
  sessionId: string,
  title: string,
  experience: string
) => {
  const res = {
    message: message ? message : null,
    locale: locale ? locale : null,
    sessionId: sessionId ? sessionId : null,
    title: title ? title : null,
    EmeraldUserType: experience ? experience : null,
  };
  // todo: Brook check if we should log this
  return JSON.stringify(res);
};

export const buildAuthTokenRequest = (purpose: string, signInType: EmeraldSigninType): AuthTokenRequest => {
  const appVer = Number(new URLSearchParams(window?.location?.search).get("appver"));
  // We might need change this value if 2403 is still a UWP.
  const getHelpV2FirstAppVer = 2403;
  const isGetHelpV2 = appVer >= getHelpV2FirstAppVer;
  const isEms = inEmsSession();
  return {
    command: 'GetAuthToken',
    context: (isGetHelpV2 && isEms) ? {
      TokenType: TokenType.AAD.toString(),
      Resource: appContext.environment.emsApplicationId,
      Scope: '.default',
      Purpose: purpose,
      SigninType: signInType,
    } : {
      TokenType: TokenType.RPS.toString(),
      SiteName: isEms ? appContext.environment.emsAuthUrlBase : appContext.environment.smcAuthUrlBase,
      SitePolicy: SMCTicketing.SitePolicy,
      Purpose: purpose,
      SigninType: signInType,
    },
  };
}
